'use client';

import { InfoCircle, SearchLg as SearchIcon } from '@untitledui/icons';
import type { FocusEventHandler, LegacyRef, PointerEventHandler, RefAttributes } from 'react';
import { forwardRef, useCallback, useContext, useRef, useState } from 'react';
import type {
  ComboBoxProps as AriaComboBoxProps,
  ListBoxProps as AriaListBoxProps,
} from 'react-aria-components/ComboBox';
import {
  ComboBox as AriaComboBox,
  Input as AriaInput,
  ListBox as AriaListBox,
  ComboBoxStateContext,
} from 'react-aria-components/ComboBox';
import type { GroupProps as AriaGroupProps } from 'react-aria-components/Group';
import { Group as AriaGroup } from 'react-aria-components/Group';

import { useResizeObserver } from '../../../hooks/use-resize-observer';
import { cx } from '../../../utils/cx';
import { HintText } from '../input/hint-text';
import { Label } from '../input/label';
import { Popover } from './popover';
import { type CommonProps, SelectContext, type SelectItemType, sizes } from './select';

/**
 * @fileoverview Combobox component for the TRM UI Kit.
 * @agents generated no-review
 *
 * Searchable select component with autocomplete functionality.
 * Generated from UntitledUI.
 *
 * @see https://www.untitledui.com/components
 */

export interface ComboBoxProps
  extends
    Omit<AriaComboBoxProps<SelectItemType>, 'children' | 'items'>,
    RefAttributes<HTMLDivElement>,
    CommonProps {
  shortcut?: boolean;
  items?: SelectItemType[];
  popoverClassName?: string;
  shortcutClassName?: string;
  inputClassName?: string;
  children: AriaListBoxProps<SelectItemType>['children'];
}

interface ComboBoxValueProps extends AriaGroupProps {
  size: 'sm' | 'md';
  shortcut: boolean;
  placeholder?: string;
  shortcutClassName?: string;
  inputClassName?: string;
  isInvalid?: boolean;
  onFocus?: FocusEventHandler;
  onPointerEnter?: PointerEventHandler;
}
/**
 * ComboBoxValue component wrapper.
 *
 * IMPORTANT: This component uses forwardRef to properly forward the ref to the AriaGroup element.
 * In React 18, forwardRef is required for ref forwarding - refs cannot be passed as regular props.
 * Without forwardRef, the ref stays null, breaking the Popover positioning (defaults to top-left corner).
 *
 * NOTE: When upgrading to React 19, forwardRef becomes optional as refs can be regular props.
 * At that point, this can be simplified to a regular function component accepting ref as a prop.
 */
const ComboBoxValue = forwardRef<HTMLDivElement, ComboBoxValueProps>(
  (
    { size, shortcut, placeholder, shortcutClassName, inputClassName, isInvalid, ...otherProps },
    ref,
  ) => {
    const state = useContext(ComboBoxStateContext);

    const value = state?.selectedItem?.value || null;
    const inputValue = state?.inputValue || null;

    const first = inputValue?.split(value?.supportingText)?.[0] || '';
    const last = inputValue?.split(first)[1];

    return (
      <AriaGroup
        ref={ref as LegacyRef<HTMLDivElement>}
        {...otherProps}
        className={({ isFocusWithin, isDisabled }) =>
          cx(
            'gap-2 relative flex w-full items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition-shadow duration-100 ease-linear ring-inset',
            isDisabled && 'cursor-not-allowed bg-disabled_subtle',
            isFocusWithin && 'ring-2 ring-brand',
            sizes[size].root,
            inputClassName,
          )
        }
      >
        {({ isDisabled }) => (
          <>
            <SearchIcon className="size-5 pointer-events-none text-fg-quaternary" />

            <div className="gap-2 relative flex w-full items-center">
              {inputValue && (
                <span
                  className="gap-2 absolute top-1/2 z-0 inline-flex w-full -translate-y-1/2 truncate"
                  aria-hidden="true"
                >
                  <p
                    className={cx(
                      'font-medium text-md text-primary',
                      isDisabled && 'text-disabled',
                    )}
                  >
                    {first}
                  </p>
                  {last && (
                    <p
                      className={cx(
                        '-ml-0.75 text-md text-tertiary',
                        isDisabled && 'text-disabled',
                      )}
                    >
                      {last}
                    </p>
                  )}
                </span>
              )}

              <AriaInput
                placeholder={placeholder}
                className="z-10 w-full appearance-none bg-transparent text-md text-transparent caret-alpha-black/90 placeholder:text-placeholder focus:outline-hidden disabled:cursor-not-allowed disabled:text-disabled disabled:placeholder:text-disabled"
              />
            </div>
            {isInvalid && (
              <InfoCircle
                className={cx(
                  'size-4 pointer-events-none absolute text-fg-error-secondary',
                  sizes[size].iconTrailing,
                )}
              />
            )}

            {shortcut && !isInvalid && (
              <div
                className={cx(
                  'inset-y-0.5 right-0.5 pl-8 absolute z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40%',
                  isDisabled && 'to-bg-disabled_subtle',
                  sizes[size].shortcut,
                  shortcutClassName,
                )}
              >
                <span
                  className={cx(
                    'rounded px-1 font-medium pointer-events-none py-px text-xs text-quaternary ring-1 ring-secondary select-none ring-inset',
                    isDisabled && 'bg-transparent text-disabled',
                  )}
                  aria-hidden="true"
                >
                  ⌘K
                </span>
              </div>
            )}
          </>
        )}
      </AriaGroup>
    );
  },
);

ComboBoxValue.displayName = 'ComboBoxValue';

export const ComboBox = ({
  placeholder = 'Search',
  shortcut = true,
  size = 'sm',
  children,
  items,
  shortcutClassName,
  inputClassName,
  ...otherProps
}: ComboBoxProps) => {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [popoverWidth, setPopoverWidth] = useState('');

  // Resize observer for popover width
  const onResize = useCallback(() => {
    if (!placeholderRef.current) return;

    const divRect = placeholderRef.current?.getBoundingClientRect();

    setPopoverWidth(divRect.width + 'px');
  }, [placeholderRef, setPopoverWidth]);

  useResizeObserver({
    ref: placeholderRef,
    box: 'border-box',
    onResize,
  });

  return (
    <SelectContext.Provider value={{ size }}>
      <AriaComboBox menuTrigger="focus" {...otherProps}>
        {(state) => (
          <div className="gap-1.5 flex flex-col">
            {otherProps.label && (
              <Label isRequired={state.isRequired} tooltip={otherProps.tooltip}>
                {otherProps.label}
              </Label>
            )}

            <ComboBoxValue
              ref={placeholderRef}
              placeholder={placeholder}
              shortcut={shortcut}
              shortcutClassName={shortcutClassName}
              inputClassName={inputClassName}
              isInvalid={state.isInvalid}
              size={size}
              // This is a workaround to correctly calculating the trigger width
              // while using ResizeObserver wasn't 100% reliable.
              onFocus={onResize}
              onPointerEnter={onResize}
            />

            <Popover
              size={size}
              triggerRef={placeholderRef}
              style={{ width: popoverWidth }}
              className={otherProps.popoverClassName}
            >
              <AriaListBox items={items} className="size-full outline-hidden">
                {children}
              </AriaListBox>
            </Popover>

            {otherProps.hint && <HintText isInvalid={state.isInvalid}>{otherProps.hint}</HintText>}
          </div>
        )}
      </AriaComboBox>
    </SelectContext.Provider>
  );
};
